import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, catchError, map, throwError } from "rxjs";
import { ViewerConfigurationService } from "../../../viewer-components/services/viewer-configuration.service";
import { AgentKitService } from "../../../agent-ui";
import type { UploadResponse, UserFileContent } from "../model/attachment.types";
import { mimeToContentFormat, contentFormatToResultType } from "../model/block.types";
import type { ContentBlockRef } from "../model/block.types";
import type { DataResult } from "../model/data.types";

/**
 * Extension → MIME type corrections applied before upload.
 * Browsers may misreport MIME for geospatial formats (e.g. .geojson as application/json,
 * .wkt as application/octet-stream). The backend uses the received content_type to
 * normalise files, so the value must be correct.
 */
const EXTENSION_MIME_MAP: Readonly<Record<string, string>> = {
  ".geojson": "application/geo+json",
  ".json": "application/json",
  ".wkt": "text/plain",
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
};

// ---------------------------------------------------------------------------
// Raw backend response types (not exported — internal mapping only)
// ---------------------------------------------------------------------------

interface BackendImageBlock {
  type: "image";
  url: string;
  id: string;
  mime_type: string;
  preview_url?: string;
}

interface BackendFileBlock {
  type: "file";
  url: string;
  id: string;
  mime_type: string;
  extras: Record<string, unknown>;
}

type BackendContentBlock = BackendImageBlock | BackendFileBlock;

interface RawUploadResponse {
  id: string;
  filename: string;
  url: string;
  preview_url?: string;
  content_block: BackendContentBlock;
}

// ---------------------------------------------------------------------------

/**
 * Uploads files to the Agents API.
 * Base URL: IViewerConfiguration.agentsApiUrl (same source as AgentsApiPrivateService).
 * Auth: Bearer token from AgentKitService.headers().
 */
@Injectable({ providedIn: "root" })
export class FileUploadService {
  private readonly http = inject(HttpClient);

  private readonly viewerConfig = inject(ViewerConfigurationService);

  private readonly agentKit = inject(AgentKitService);

  private get baseUrl(): string {
    const url = this.viewerConfig.getViewerConfiguration()?.agentsApiUrl ?? "";
    return url.replace(/\/+$/, "");
  }

  private get headers(): HttpHeaders {
    const agentKitHeaders = this.agentKit.headers();
    return new HttpHeaders({ ...agentKitHeaders });
  }

  /**
   * Appends the current auth credentials as query parameters so the URL can be
   * used in contexts that cannot set request headers (e.g. <img src="...">).
   *
   * Mapping rules (first match wins):
   *   Authorization: Bearer <t>  →  ?token=<t>
   *   x-api-key: <k>             →  ?api_key=<k>
   *   (no recognised header)     →  URL returned unchanged
   */
  authenticatedUrl(url: string): string {
    if (!url) return url;
    const hdrs = this.agentKit.headers();
    const auth = hdrs.Authorization ?? hdrs.authorization;
    if (auth?.startsWith("Bearer ")) {
      return this.appendParam(url, "token", auth.slice(7));
    }
    const apiKey = hdrs["x-api-key"] ?? hdrs["X-Api-Key"];
    if (apiKey) {
      return this.appendParam(url, "api_key", apiKey);
    }
    return url;
  }

  private appendParam(url: string, name: string, value: string): string {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}${name}=${encodeURIComponent(value)}`;
  }

  /**
   * Maps a raw backend content_block to the AG-UI native UserFileContent format.
   * All extras fields are spread into metadata to avoid data loss.
   */
  private mapContentBlock(raw: BackendContentBlock): UserFileContent {
    if (raw.type === "image") {
      return {
        type: "image",
        source: { type: "url", value: raw.url, mimeType: raw.mime_type },
        metadata: { id: raw.id, preview_url: raw.preview_url },
      };
    }
    return {
      type: "document",
      source: { type: "url", value: raw.url, mimeType: raw.mime_type },
      metadata: { ...raw.extras, id: raw.id },
    };
  }

  /**
   * Uploads a single file and returns the UploadResponse from the backend.
   * Always sends generate_preview=true; the backend ignores it for non-image types.
   * Corrects the MIME type from the file extension before sending.
   * Maps the raw backend content_block to AG-UI native format.
   */
  upload(file: File, threadId: string): Observable<UploadResponse> {
    const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
    const correctedMime = EXTENSION_MIME_MAP[ext] ?? file.type;
    const uploadFile = correctedMime !== file.type ? new File([file], file.name, { type: correctedMime }) : file;

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("thread_id", threadId);

    return this.http
      .post<RawUploadResponse>(`${this.baseUrl}/api/files/upload?generate_preview=true`, formData, {
        headers: this.headers,
      })
      .pipe(
        map((raw) => ({ ...raw, content_block: this.mapContentBlock(raw.content_block) })),
        catchError((err) => {
          console.error("FileUploadService: upload failed", file.name, err?.error ?? err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Fetches the raw content of a stored file and maps it to a DataResult.
   * Used by FileBlockCardComponent to lazy-load file content for rendering.
   * Sends Authorization via header (not query param) since this is an XHR request.
   */
  fetchFileContent(
    url: string,
    mimeType: string,
    ref: Pick<ContentBlockRef, "filename" | "size_bytes">,
  ): Observable<DataResult> {
    const filename = ref.filename;
    const sizeBytes = ref.size_bytes ?? undefined;
    const ct = contentFormatToResultType(mimeToContentFormat(mimeType));
    if (ct === "geotiff") {
      return this.http.get(url, { headers: this.headers, responseType: "arraybuffer" }).pipe(
        map(
          (buf): DataResult => ({
            type: "geotiff",
            geotiff: this.arrayBufferToBase64(buf),
            filename,
            sizeBytes,
          }),
        ),
        catchError((err) => {
          console.error("FileUploadService: fetchFileContent failed", url, err?.error ?? err);
          return throwError(() => err);
        }),
      );
    }
    return this.http.get(url, { headers: this.headers, responseType: "text" }).pipe(
      map((text): DataResult => {
        if (ct === "geojson") {
          try {
            return {
              type: "geojson",
              geojson: JSON.parse(text),
              filename,
              sizeBytes,
            };
          } catch {
            return { type: "text", text };
          }
        }
        if (ct === "json") {
          try {
            return { type: "json", json: JSON.parse(text), filename, sizeBytes };
          } catch {
            return { type: "text", text };
          }
        }
        return { type: "text", text, filename, sizeBytes };
      }),
      catchError((err) => {
        console.error("FileUploadService: fetchFileContent failed", url, err?.error ?? err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Fetches a preview_url endpoint which returns text/plain containing a data URL.
   * Used by FileBlockCardComponent to show image thumbnails from file-store previews.
   */
  fetchPreviewDataUrl(previewUrl: string): Observable<string> {
    return this.http.get(previewUrl, { headers: this.headers, responseType: "text" }).pipe(
      catchError((err) => {
        console.error("FileUploadService: fetchPreviewDataUrl failed", previewUrl, err?.error ?? err);
        return throwError(() => err);
      }),
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Deletes a previously uploaded file.
   * DELETE {agentsApiUrl}/api/files/{threadId}/{fileId}
   */
  deleteFile(fileId: string, threadId: string): Observable<void> {
    return this.http
      .delete(`${this.baseUrl}/api/files/${encodeURIComponent(threadId)}/${encodeURIComponent(fileId)}`, {
        headers: this.headers,
      })
      .pipe(
        map(() => undefined),
        catchError((err) => {
          console.error("FileUploadService: delete failed", fileId, err);
          return throwError(() => err);
        }),
      );
  }
}
